<script setup lang="ts">
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import useEmail from "@/composables/useEmail";
import { EmailTypes } from "@/enums/emailTypes";
import FieldSet from "@/modules/companies/components/FieldSet.vue";
import useUserEmail from "@/modules/users/composables/useUserEmail";
import { useQueryClient } from "@tanstack/vue-query";
import { reactive, ref } from "vue";
import { EmailConverted } from "./UserNotificationsForm.vue";

interface Props {
  userId: number;
  emailSelected?: EmailConverted;
}

interface Emits {
  (e: "handle-action-modal"): void;
}

const props = defineProps<Props>();

const emits = defineEmits<Emits>();

const queryClient = useQueryClient();

const emailForm = reactive({
  email: props.emailSelected?.email || "",
  type: EmailTypes.Personal,
});

const { createEmailMutation } = useUserEmail({
  immediate: false,
});

const { updateEmailMutation } = useEmail({
  immediate: false,
  params: {
    emailId: props.emailSelected?.id,
  },
});

const emailSubmit = async () => {
  const data = await createEmailMutation.mutateAsync({
    email: emailForm.email,
    type: emailForm.type,
    id: props.userId,
  });

  if (data) {
    await queryClient.invalidateQueries(["userEmails", props.userId]);
    await queryClient.refetchQueries(["user", props.userId]);

    resetEmailForm();
    emits("handle-action-modal");
  }
};

const emailEdit = async () => {
  const data = await updateEmailMutation.mutateAsync({
    email: emailForm.email,
    type: emailForm.type,
    emailId: props.emailSelected?.id,
  });

  if (data) {
    await queryClient.invalidateQueries(["userEmails", props.userId]);
    await queryClient.refetchQueries(["user", props.userId]);

    resetEmailForm();
    emits("handle-action-modal");
  }
};

const resetEmailForm = () => {
  emailForm.email = "";
  emailForm.type = EmailTypes.Personal;
};
</script>

<template>
  <div class="grid gap-4">
    <h2>{{ !emailSelected ? "Crear email" : "Editar email" }}</h2>
    <form
      class="grid gap-4"
      @submit.prevent="() => (!emailSelected ? emailSubmit() : emailEdit())"
    >
      <StyledInput
        v-model="emailForm.email"
        label="Email"
        type="email"
        placeholder="ej. ejemplo@email.com"
        class="w-full"
      />

      <div>
        <StyledButton
          v-if="!emailSelected"
          :disabled="!emailForm.email"
          type="submit"
          class="bg-[#253C70] text-white flex items-center gap-[0.75rem] !px-2 ml-auto"
        >
          Crear
        </StyledButton>

        <StyledButton
          v-else
          :disabled="!emailForm.email"
          type="submit"
          class="bg-[#253C70] text-white flex items-center gap-[0.75rem] !px-2 ml-auto"
        >
          Editar
        </StyledButton>
      </div>
    </form>
  </div>
</template>

<style scoped></style>
